import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../hooks/useApi';
import PainelFotosPendentes from '../../components/PainelFotosPendentes';
import {
  PackageIcon, ReceiptIcon, CurrencyIcon, EyeIcon,
  PlusIcon, ArrowRightIcon, ClockIcon, CheckIcon,
  TruckIcon, RefreshIcon, ZapIcon, StarIcon
} from '../../components/Icons';

export default function DashboardFornecedor() {
  const navigate       = useNavigate();
  const { utilizador } = useAuth();
  const [stats,      setStats]      = useState(null);
  const [load,       setLoad]       = useState(true);
  const [avaliacoes, setAvaliacoes] = useState([]);

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoad(true);
    try {
      const [anuncios, reservas, avals] = await Promise.all([
        apiFetch('/fornecedor/anuncios').catch(()=>({anuncios:[]})),
        apiFetch('/fornecedor/reservas').catch(()=>({reservas:[]})),
        apiFetch('/fornecedor/avaliacoes').catch(()=>({avaliacoes:[]})),
      ]);
      const an = anuncios.anuncios   || [];
      const rs = reservas.reservas   || [];
      setAvaliacoes(avals.avaliacoes || []);
      setStats({
        totalAnuncios:   an.length,
        anunciosAtivos:  an.filter(a=>a.estado==='ativo').length,
        anunciosPendentes: an.filter(a=>a.estado==='pendente').length,
        totalViews:      an.reduce((s,a)=>s+(a.total_views||0),0),
        totalReservas:   rs.length,
        reservasPendentes: rs.filter(r=>r.estado==='pendente').length,
        reservasAtivas:  rs.filter(r=>['confirmada','entregue','em_uso'].includes(r.estado)).length,
        totalGanhos:     rs.filter(r=>r.escrow_liberado).reduce((s,r)=>s+parseFloat(r.valor_aluguer||0),0),
        anunciosRecentes: an.slice(0,4),
        reservasRecentes: rs.slice(0,5),
      });
    } catch { setStats(null); }
    finally { setLoad(false); }
  };

  const estadoReserva = {
    pendente:   { label:'Pendente',   cls:'badge-warning' },
    confirmada: { label:'Confirmada', cls:'badge-info' },
    entregue:   { label:'Entregue',   cls:'badge-success' },
    em_uso:     { label:'Em uso',     cls:'badge-success' },
    devolvida:  { label:'Devolvida',  cls:'badge-neutral' },
    concluida:  { label:'Concluída',  cls:'badge-success' },
    cancelada:  { label:'Cancelada',  cls:'badge-danger' },
    disputa:    { label:'Disputa',    cls:'badge-danger' },
  };

  const s = stats || {};

  return (
    <>
      <div className="topbar">
        <div>
          <div className="topbar-title">Olá, {utilizador?.nome?.split(' ')[0] || 'Fornecedor'}</div>
          <div className="topbar-sub">Aqui está o resumo do teu negócio</div>
        </div>
        <div className="topbar-actions">
          <button className="btn btn-outline" onClick={carregar} style={{display:'flex',alignItems:'center',gap:'6px'}}>
            <RefreshIcon size={14}/> Actualizar
          </button>
          <button className="btn btn-primary" onClick={()=>navigate('/fornecedor/publicar')} style={{display:'flex',alignItems:'center',gap:'6px'}}>
            <PlusIcon size={14}/> Novo anúncio
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Produtos de bulk upload a aguardar fotos */}
        <PainelFotosPendentes />

        {/* KYC warning */}
        {utilizador?.kyc_estado !== 'verificado' && (
          <div style={{background:'#FFFBEB',border:'1px solid #FDE68A',borderRadius:'10px',padding:'12px 16px',marginBottom:'18px',display:'flex',alignItems:'center',gap:'10px',fontSize:'13px'}}>
            <ClockIcon size={16} color='#D97706'/>
            <div style={{flex:1}}>
              <span style={{fontWeight:'600',color:'#92400E'}}>Verificação KYC pendente · </span>
              <span style={{color:'#92400E'}}>Completa a verificação de identidade para publicar anúncios.</span>
            </div>
            <button className="btn btn-sm" style={{background:'#D97706',color:'#fff',border:'none'}} onClick={()=>navigate('/perfil')}>
              Verificar agora <ArrowRightIcon size={12}/>
            </button>
          </div>
        )}

        {/* Stats */}
        <div className="stats-grid" style={{marginBottom:'20px'}}>
          {[
            { icon:<PackageIcon/>, label:'Anúncios', value:load?'…':s.totalAnuncios||0, sub:`${s.anunciosAtivos||0} activos · ${s.anunciosPendentes||0} pendentes`, bg:'#FFF7ED', ic:'#F97316' },
            { icon:<EyeIcon/>,     label:'Visualizações', value:load?'…':(s.totalViews||0).toLocaleString(), sub:'Total acumulado', bg:'#EFF6FF', ic:'#2563EB' },
            { icon:<ReceiptIcon/>, label:'Reservas',  value:load?'…':s.totalReservas||0, sub:`${s.reservasAtivas||0} activas · ${s.reservasPendentes||0} pendentes`, bg:'#F0FDF4', ic:'#16A34A' },
            { icon:<CurrencyIcon/>,label:'Ganhos',    value:load?'…':`${(s.totalGanhos||0).toLocaleString()} Kz`, sub:'Valor recebido', bg:'#F5F3FF', ic:'#7C3AED' },
          ].map((c,i)=>(
            <div key={i} className="stat-card">
              <div className="stat-icon" style={{background:c.bg}}>
                {React.cloneElement(c.icon,{size:18,color:c.ic})}
              </div>
              <div className="stat-value" style={{marginTop:'8px'}}>{c.value}</div>
              <div className="stat-label">{c.label}</div>
              <div style={{fontSize:'11px',color:'var(--text-muted)',marginTop:'2px'}}>{c.sub}</div>
            </div>
          ))}
        </div>

        <div className="grid-2">
          {/* Anúncios */}
          <div className="card">
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'14px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <PackageIcon size={15} color='var(--orange)'/>
                <span style={{fontSize:'13px',fontWeight:'600'}}>Os meus anúncios</span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={()=>navigate('/fornecedor/anuncios')} style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'11px'}}>
                Ver todos <ArrowRightIcon size={12}/>
              </button>
            </div>
            {load ? <div style={{textAlign:'center',padding:'30px',color:'var(--text-muted)',fontSize:'13px'}}>A carregar...</div>
            : s.anunciosRecentes?.length === 0 ? (
              <div style={{textAlign:'center',padding:'30px'}}>
                <PackageIcon size={28} color='var(--border)'/><br/>
                <div style={{fontSize:'13px',color:'var(--text-muted)',margin:'8px 0'}}>Ainda sem anúncios</div>
                <button className="btn btn-primary btn-sm" onClick={()=>navigate('/fornecedor/publicar')} style={{display:'inline-flex',alignItems:'center',gap:'5px'}}>
                  <PlusIcon size={13}/> Publicar primeiro
                </button>
              </div>
            ) : s.anunciosRecentes?.map((a,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 0',borderBottom:i<s.anunciosRecentes.length-1?'1px solid var(--border)':'none',cursor:'pointer'}}
                onClick={()=>navigate('/fornecedor/anuncios')}>
                <div style={{width:'38px',height:'38px',borderRadius:'8px',overflow:'hidden',flexShrink:0,background:'linear-gradient(135deg,#fde8d8,#fed7b0)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  {a.fotos?.[0] ? <img src={a.fotos[0]} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>e.target.style.display='none'}/> : <PackageIcon size={16} color='#F97316'/>}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:'12px',fontWeight:'600',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.titulo}</div>
                  <div style={{fontSize:'11px',color:'var(--text-muted)'}}>{parseFloat(a.preco_dia||0).toLocaleString()} Kz/dia · {a.total_views||0} views</div>
                </div>
                <span className={`badge badge-${a.estado==='ativo'?'success':a.estado==='pendente'?'warning':'neutral'}`} style={{fontSize:'10px'}}>
                  {a.estado==='ativo'?'Activo':a.estado==='pendente'?'Pendente':'Pausado'}
                </span>
              </div>
            ))}
          </div>

          {/* Reservas recentes */}
          <div className="card">
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'14px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <ReceiptIcon size={15} color='var(--orange)'/>
                <span style={{fontSize:'13px',fontWeight:'600'}}>Reservas recentes</span>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={()=>navigate('/fornecedor/reservas')} style={{display:'flex',alignItems:'center',gap:'4px',fontSize:'11px'}}>
                Ver todas <ArrowRightIcon size={12}/>
              </button>
            </div>
            {load ? <div style={{textAlign:'center',padding:'30px',color:'var(--text-muted)',fontSize:'13px'}}>A carregar...</div>
            : s.reservasRecentes?.length === 0 ? (
              <div style={{textAlign:'center',padding:'30px',color:'var(--text-muted)'}}>
                <ReceiptIcon size={28} color='var(--border)'/><br/>
                <span style={{fontSize:'13px',marginTop:'8px',display:'block'}}>Sem reservas ainda</span>
              </div>
            ) : s.reservasRecentes?.map((r,i)=>{
              const est = estadoReserva[r.estado] || { label:r.estado, cls:'badge-neutral' };
              return (
                <div key={i} style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 0',borderBottom:i<s.reservasRecentes.length-1?'1px solid var(--border)':'none',cursor:'pointer'}}
                  onClick={()=>navigate('/fornecedor/reservas')}>
                  <div style={{width:'34px',height:'34px',borderRadius:'50%',background:'var(--brand-mid)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',fontWeight:'700',color:'#fff',flexShrink:0}}>
                    {(r.cliente_nome||'?')[0].toUpperCase()}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:'12px',fontWeight:'600',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.titulo||'Produto'}</div>
                    <div style={{fontSize:'11px',color:'var(--text-muted)'}}>{r.cliente_nome} · {parseFloat(r.valor_total||0).toLocaleString()} Kz</div>
                  </div>
                  <span className={`badge ${est.cls}`} style={{fontSize:'10px'}}>{est.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Avaliações recebidas */}
        {avaliacoes.length > 0 && (
          <div className="card" style={{marginTop:'16px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'14px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <StarIcon size={15} color='#f59e0b'/>
                <span style={{fontSize:'13px',fontWeight:'600'}}>Avaliações recebidas</span>
              </div>
              <span style={{fontSize:'12px',color:'var(--text-muted)'}}>
                Média: {utilizador?.rating_medio ? parseFloat(utilizador.rating_medio).toFixed(1) : '—'} · {avaliacoes.length} avaliação{avaliacoes.length!==1?'ões':''}
              </span>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
              {avaliacoes.slice(0,5).map((av,i) => (
                <div key={av.id||i} style={{display:'flex',gap:'10px',alignItems:'flex-start',padding:'10px',background:'var(--surface)',borderRadius:'8px'}}>
                  <div style={{width:'32px',height:'32px',borderRadius:'50%',background:'var(--orange)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'12px',color:'#fff',fontWeight:'700',flexShrink:0,overflow:'hidden'}}>
                    {av.avaliador_foto
                      ? <img src={av.avaliador_foto} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>e.target.style.display='none'}/>
                      : (av.avaliador_nome||'?')[0].toUpperCase()
                    }
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'6px',marginBottom:'3px'}}>
                      <span style={{fontSize:'12px',fontWeight:'600',color:'var(--text-primary)'}}>{av.avaliador_nome||'Cliente'}</span>
                      <div style={{display:'flex',gap:'2px'}}>
                        {[1,2,3,4,5].map(n=>(
                          <StarIcon key={n} size={11} color={av.nota>=n?'#f59e0b':'var(--border)'} fill={av.nota>=n?'#f59e0b':'none'}/>
                        ))}
                      </div>
                    </div>
                    {av.anuncio_titulo && <div style={{fontSize:'11px',color:'var(--text-muted)',marginBottom:'3px'}}>{av.anuncio_titulo}</div>}
                    {av.comentario && <div style={{fontSize:'12px',color:'var(--text-secondary)',lineHeight:'1.5',fontStyle:'italic'}}>"{av.comentario}"</div>}
                    <div style={{fontSize:'10px',color:'var(--text-muted)',marginTop:'4px'}}>{new Date(av.created_at).toLocaleDateString('pt-PT')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Acções rápidas */}
        <div className="card" style={{marginTop:'16px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'14px'}}>
            <ZapIcon size={15} color='var(--orange)'/>
            <span style={{fontSize:'13px',fontWeight:'600'}}>Acções rápidas</span>
          </div>
          <div className="grid-4">
            {[
              { icon:<PlusIcon size={20} color='#F97316'/>,    label:'Publicar anúncio', sub:'Novo bem para alugar', path:'/fornecedor/publicar', bg:'#FFF7ED' },
              { icon:<ReceiptIcon size={20} color='#16A34A'/>, label:'Ver reservas',     sub:'Gerir pedidos', path:'/fornecedor/reservas', bg:'#F0FDF4' },
              { icon:<CurrencyIcon size={20} color='#7C3AED'/>,label:'Os meus ganhos',  sub:'Histórico financeiro', path:'/fornecedor/ganhos', bg:'#F5F3FF' },
              { icon:<StarIcon size={20} color='#D97706'/>,    label:'O meu perfil',     sub:'KYC e definições', path:'/perfil', bg:'#FFFBEB' },
            ].map((a,i)=>(
              <div key={i} onClick={()=>navigate(a.path)} style={{padding:'14px',borderRadius:'10px',cursor:'pointer',background:a.bg,border:'1px solid var(--border)',transition:'all 0.15s',textAlign:'center'}}
                onMouseOver={e=>{e.currentTarget.style.transform='translateY(-2px)';e.currentTarget.style.boxShadow='var(--shadow)';}}
                onMouseOut={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='none';}}>
                <div style={{marginBottom:'8px'}}>{a.icon}</div>
                <div style={{fontSize:'12px',fontWeight:'600',color:'var(--text-primary)',marginBottom:'2px'}}>{a.label}</div>
                <div style={{fontSize:'11px',color:'var(--text-muted)'}}>{a.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
